import type {JsonSchema} from '@jsonforms/core'

import type {AppThunk} from '../../../../actions/types'
import type {PipelineId} from '../../../../types'
import {pipelinesApi} from '../services/pipelinesApi'
import type {Pipeline} from '../services/pipelinesApi.types'
import {normalizeSchemaProperties, type SchemaProperty} from '../utils/normalizeSchemaProperties'
import {getValidationErrorMessage} from '../utils/validators'
import type {PipelineInfo, ValidatorAdditionalProps} from '../utils/validators.types'

import {PLACEHOLDER_PIPELINE_YAML} from './EditPipelinePage.consts'
import {
  getAllFormsToValidate,
  getDraft,
  getFeatures,
  getSteps,
  validatorSelectors,
} from './EditPipelinePage.selectors'
import {getBuildFeatureFormId, parsePipeline} from './EditPipelinePage.utils'
import {
  getSchemaErrorFieldName,
  getJsonSchemaErrors,
  translateSchemaError,
} from './SettingsSidebarContent/JobSettingsSidebar/JobSteps/JobStep/JobStepJsonForm/validation'
import {
  getSchemaDefaults,
  getStepRunnerJsonSchema,
} from './SettingsSidebarContent/JobSettingsSidebar/JobSteps/JobSteps.utils'
import type {DeleteJobPayload} from './slices/EditPipelinePage.job.slice'
import {
  pipelineDraft,
  pipelineErrors,
  pipelineForm,
  pipelineSecrets,
  pipelineStepSecrets,
  pipelineYaml,
} from './slices/EditPipelinePage.slices'
import type {PipelineFormParams} from './slices/EditPipelinePage.slices.types'
import {FormType} from './slices/EditPipelinePage.slices.types'

export const validateField =
  (
    formParams: PipelineFormParams,
    fieldName: string,
    pipelineNames?: PipelineInfo[],
  ): AppThunk<boolean> =>
  (dispatch, getState) => {
    const {pipelineId, branch, jobId, type, id} = formParams
    dispatch(
      pipelineErrors.actions.clear({
        pipelineId,
        branch,
        jobId,
        formType: type,
        formId: id,
        fieldName,
      }),
    )
    const state = getState()
    const additionalProps: ValidatorAdditionalProps = {pipelineNames: pipelineNames ?? []}
    const validators = validatorSelectors[formParams.type]?.[fieldName]?.(
      state,
      formParams,
      additionalProps,
    )
    const message = validators != null ? getValidationErrorMessage(validators) : ''
    if (message) {
      dispatch(
        pipelineErrors.actions.add({
          pipelineId,
          branch,
          jobId,
          formType: type,
          formId: id,
          fieldName,
          message,
        }),
      )
      return false
    }

    return true
  }

const validateSchemaForm =
  (formParams: PipelineFormParams, schema?: JsonSchema, data?: object): AppThunk<boolean> =>
  dispatch => {
    const {pipelineId, branch, jobId, id, type} = formParams

    dispatch(
      pipelineErrors.actions.clear({
        pipelineId,
        branch,
        jobId,
        formType: type,
        formId: id,
      }),
    )

    const customValid = Object.keys(validatorSelectors[type] ?? {})
      .map(fieldName => dispatch(validateField(formParams, fieldName)))
      .every(Boolean)

    if (schema == null || data == null) {
      return customValid
    }

    const schemaErrors = getJsonSchemaErrors(schema, data)
    schemaErrors.forEach(error =>
      dispatch(
        pipelineErrors.actions.add({
          pipelineId,
          branch,
          jobId,
          formType: type,
          formId: id,
          fieldName: getSchemaErrorFieldName(error),
          message: translateSchemaError(error),
        }),
      ),
    )
    return customValid && schemaErrors.length === 0
  }

const validateSchemaBackedForm =
  (
    formParams: PipelineFormParams,
    getPayload: (
      state: Parameters<typeof getSteps>[0],
      params: PipelineFormParams,
    ) => {entity?: unknown; schema?: JsonSchema},
  ): AppThunk<boolean> =>
  (dispatch, getState) => {
    const {entity, schema} = getPayload(getState(), formParams)
    const normalizedSchema =
      schema != null ? normalizeSchemaProperties(schema as SchemaProperty) : undefined
    const data =
      entity != null && normalizedSchema != null
        ? {...getSchemaDefaults(normalizedSchema), ...entity}
        : undefined

    return dispatch(validateSchemaForm(formParams, normalizedSchema, data))
  }

const validateStepForm = (formParams: PipelineFormParams): AppThunk<boolean> =>
  validateSchemaBackedForm(formParams, (state, {pipelineId, jobId, id, branch}) => {
    const step = getSteps(state, pipelineId, branch, jobId!)?.[Number(id)]
    const jobRunners = pipelinesApi.endpoints.getPipelineRunnersJsonFromXMLs.select()(
      state as never,
    ).data
    return {
      entity: step,
      schema: getStepRunnerJsonSchema(jobRunners, step?.type),
    }
  })

const validateBuildFeatureForm = (formParams: PipelineFormParams): AppThunk<boolean> =>
  validateSchemaBackedForm(formParams, (state, {pipelineId, jobId, id, branch}) => {
    const feature = (getFeatures(state, pipelineId, branch, jobId!) ?? []).find(
      (item, index) => getBuildFeatureFormId(item.type ?? '', index) === id,
    )
    const featuresSchema = pipelinesApi.endpoints.getPipelineFeaturesJsonFromXMLs.select()(
      state as never,
    ).data
    return {
      entity: feature,
      schema: feature?.type != null ? featuresSchema?.[feature.type]?.jsonSchema : undefined,
    }
  })

export const validateForm =
  (formParams: PipelineFormParams, pipelineNames?: PipelineInfo[]): AppThunk<boolean> =>
  dispatch => {
    if (formParams.type === FormType.STEP) {
      return dispatch(validateStepForm(formParams))
    }
    if (formParams.type === FormType.BUILD_FEATURE) {
      return dispatch(validateBuildFeatureForm(formParams))
    }
    const additionalProps: ValidatorAdditionalProps = {pipelineNames}
    const results = Object.keys(validatorSelectors[formParams.type] ?? {}).map(fieldName =>
      dispatch(validateField(formParams, fieldName, additionalProps.pipelineNames)),
    )
    return results.every(Boolean)
  }

export const validateAllForms =
  (
    pipelineId: PipelineId,
    branch: string,
    filter: (form: PipelineFormParams) => boolean = () => true,
  ): AppThunk<boolean> =>
  (dispatch, getState) => {
    const results = getAllFormsToValidate(getState(), pipelineId, branch)
      .filter(filter)
      .map(formParams => dispatch(validateForm(formParams)))
    return results.every(Boolean)
  }

export const deleteJob =
  (payload: DeleteJobPayload): AppThunk =>
  dispatch => {
    dispatch(pipelineDraft.actions.deleteJob(payload))
    const {id, jobId, branch} = payload
    dispatch(pipelineErrors.actions.clear({pipelineId: id, branch, jobId}))
  }

const clearPipelineEditState =
  (pipelineId: PipelineId, branch: string): AppThunk =>
  dispatch => {
    dispatch(pipelineErrors.actions.clear({pipelineId, branch}))
    dispatch(pipelineForm.actions.closeAll(pipelineId))
    dispatch(pipelineSecrets.actions.unsetAll({pipelineId, branch}))
    dispatch(pipelineStepSecrets.actions.unsetAll({pipelineId, branch}))
  }

export const discardPipelineDraft =
  (pipelineId: PipelineId, branch: string): AppThunk =>
  dispatch => {
    dispatch(pipelineDraft.actions.reset({id: pipelineId, branch}))
    dispatch(pipelineYaml.actions.reset({id: pipelineId, branch}))
    dispatch(clearPipelineEditState(pipelineId, branch))
  }

export const commitPipelineDraft =
  (pipelineId: PipelineId, branch: string, pipeline: Pipeline): AppThunk =>
  dispatch => {
    dispatch(pipelineDraft.actions.set({pipeline, branch}))
    dispatch(clearPipelineEditState(pipelineId, branch))
  }

export const createPipelineConfiguration =
  (pipelineId: PipelineId, branch: string): AppThunk =>
  (dispatch, getState) => {
    const draft = getDraft(getState(), pipelineId, branch)
    if (draft == null) {
      return
    }
    const settings = parsePipeline(PLACEHOLDER_PIPELINE_YAML) ?? draft.settings
    dispatch(
      pipelineDraft.actions.updateDraft({id: pipelineId, draft: {...draft, settings}, branch}),
    )
  }
